import type { GcSuggestion, GcPriorityKey } from '@elva/ui';
import type { CcSuggestion, CcPriority } from '@elva/ui';
import type { IconKind } from '@elva/ui';
import { growthGet, growthList, growthPost, growthPostAsync } from '@/data/http';
import type { BeSuggestion } from '@/data/marketingApiTypes';
import { deepCamel, num, stableNumericId, str } from '@/data/adapt';
import { enumLower } from '@/data/adapt';

export interface SuggestedCampaignsData {
  gcItems: GcSuggestion[];
  ccItems: CcSuggestion[];
  idMap: Record<number, string>;
}

function suggestionBudgetDollars(row: BeSuggestion): number {
  if (row.projected_monthly_budget_cents != null) {
    return Math.round(row.projected_monthly_budget_cents / 100);
  }
  if (row.est_budget_cents != null) return Math.round(row.est_budget_cents / 100);
  if (row.est_budget != null) return Math.round(row.est_budget);
  if (row.spend_cents != null) return Math.round(row.spend_cents / 100);
  return num(row.spend);
}

function suggestionConfidence(row: BeSuggestion): number {
  return num(row.projected_confidence_score ?? row.confidence);
}

function suggestionName(row: BeSuggestion): string {
  return str(row.projected_name ?? row.name ?? row.service);
}

function suggestionReasoning(row: BeSuggestion): string {
  const summary = row.suggestion_data?.summary?.trim();
  if (summary) return summary;
  return str(row.reasoning, '—');
}

function suggestionNextActionSignals(row: BeSuggestion): Array<{ icon: IconKind; text: string }> {
  const fromNextActions =
    row.suggestion_data?.next_actions?.map((text) => ({
      icon: 'sparkle' as IconKind,
      text,
    })) ?? [];
  if (fromNextActions.length) return fromNextActions;
  return (row.clinic_signals ?? []).map((s) => ({
    icon: (s.icon ?? 'sparkle') as IconKind,
    text: str(s.text),
  }));
}

function adaptGcSuggestion(row: BeSuggestion): { item: GcSuggestion; backendId: string } {
  const backendId = String(row.id ?? '');
  const ad = deepCamel<Record<string, unknown>>(row.ad_preview ?? row.ad ?? {});
  return {
    backendId,
    item: {
      id: stableNumericId(backendId),
      name: suggestionName(row),
      confidence: suggestionConfidence(row),
      priority: (enumLower(row.priority, 'medium') as GcPriorityKey) ?? 'medium',
      estBudget: suggestionBudgetDollars(row),
      estCPC: num(row.est_cpc),
      estBookings: str(row.est_bookings ?? row.patients, '—'),
      estCostPerPatient: str(row.est_cost_per_patient ?? row.cost_per_patient, '—'),
      keywords: Array.isArray(row.keywords)
        ? row.keywords.map((k) => (typeof k === 'string' ? k : str((k as Record<string, unknown>).keyword)))
        : [],
      adPreview: {
        headline: str(ad.headline, suggestionName(row)),
        desc: str(ad.desc ?? ad.description, suggestionReasoning(row)),
      },
      marketSignals: (row.market_signals ?? []).map((s) => ({
        icon: (s.icon ?? 'pulse') as IconKind,
        text: str(s.text),
      })),
      clinicSignals: suggestionNextActionSignals(row),
      reasoning: suggestionReasoning(row),
    },
  };
}

function adaptCcSuggestion(row: BeSuggestion): CcSuggestion {
  const ad = deepCamel<Record<string, unknown>>(row.ad ?? row.ad_preview ?? {});
  const spend = suggestionBudgetDollars(row);

  return {
    id: stableNumericId(String(row.id ?? '')),
    service: str(row.projected_service_category ?? row.service ?? row.projected_name ?? row.name),
    icon: str(row.icon, '🦷'),
    priority: (enumLower(row.priority, 'medium') as CcPriority) ?? 'medium',
    spend,
    patients: str(row.patients ?? row.est_bookings, '—'),
    costPerPatient: str(row.cost_per_patient ?? row.est_cost_per_patient, '—'),
    avgCaseValue: num(row.avg_case_value),
    roi: str(row.roi, '—'),
    roiEstimateMin: num(row.roi_estimate_min),
    roiEstimateMax: num(row.roi_estimate_max),
    radiusMiles: num(row.radius_miles, 7),
    confidence: suggestionConfidence(row),
    who: str(row.who, suggestionName(row)),
    whyNow: suggestionReasoning(row),
    keywords: Array.isArray(row.keywords)
      ? row.keywords.map((k) => (typeof k === 'string' ? k : str((k as Record<string, unknown>).keyword)))
      : [],
    ad: {
      headline: str(ad.headline, suggestionName(row)),
      url: str(ad.url, '—'),
      description: str(ad.description ?? ad.desc, suggestionReasoning(row)),
      extensions: (ad.extensions as string[] | undefined) ?? [],
    },
    landingHighlights:
      row.suggestion_data?.next_actions ??
      row.landing_highlights ??
      [],
  };
}

export async function fetchSuggestedCampaigns(): Promise<SuggestedCampaignsData> {
  const { items: rows } = await growthList<BeSuggestion>('/suggested-campaigns/', {
    page: 1,
    limit: 100,
  });

  const gcItems: GcSuggestion[] = [];
  const ccItems: CcSuggestion[] = [];
  const idMap: Record<number, string> = {};

  for (const row of rows) {
    const { item, backendId } = adaptGcSuggestion(row);
    gcItems.push(item);
    ccItems.push(adaptCcSuggestion(row));
    idMap[item.id] = backendId;
  }

  return { gcItems, ccItems, idMap };
}

export async function dismissSuggestedCampaign(id: string) {
  return growthPost<{
    action?: string;
    suggestion_id?: string;
    clinic_id?: number;
    status?: string;
  }>(`/suggested-campaigns/${id}/dismiss/`);
}

export async function acceptSuggestedCampaign(body: {
  id: string;
  mode?: 'autopilot' | 'review';
}) {
  return growthPostAsync(`/suggested-campaigns/${body.id}/accept/`, { mode: body.mode ?? 'review' });
}

export async function refreshSuggestedCampaigns() {
  return growthPostAsync('/suggested-campaigns/refresh/');
}

/** Live reads `service_category`, `monthly_budget_cents`, `instructions`, `radius_miles`. */
export async function generateCustomCampaign(body: {
  serviceCategory: string;
  monthlyBudgetCents?: number;
  instructions?: string;
  radiusMiles?: number;
}) {
  return growthPostAsync('/campaigns/custom/', {
    service_category: body.serviceCategory,
    monthly_budget_cents: body.monthlyBudgetCents,
    instructions: body.instructions,
    radius_miles: body.radiusMiles,
  });
}
